import type {
  CreateGalleryInput,
  CreateContactMessageInput,
  CreateInquiryInput,
  CreateNewsInput,
  CreatePartnerInput,
  CreateProductInput,
  Gallery,
  ContactMessage,
  Inquiry,
  News,
  Partner,
  Product,
  TeamMember,
  User,
  UpdateNewsInput,
  UpdateProductInput,
} from './index';

export interface Repository<T, CreateInput, UpdateInput> {
  list(): Promise<T[]>;
  get(id: string): Promise<T | null>;
  create(input: CreateInput): Promise<T>;
  update(id: string, input: UpdateInput): Promise<T>;
  remove(id: string): Promise<void>;
}

export interface ProductRepository extends Repository<Product, CreateProductInput, UpdateProductInput> {}
export interface NewsRepository extends Repository<News, CreateNewsInput, UpdateNewsInput> {}
export interface GalleryRepository extends Repository<Gallery, CreateGalleryInput, Partial<CreateGalleryInput>> {}
export interface PartnerRepository extends Repository<Partner, CreatePartnerInput, Partial<CreatePartnerInput>> {}
export interface InquiryRepository extends Repository<Inquiry, CreateInquiryInput, Partial<CreateInquiryInput>> {}
export interface ContactMessageRepository extends Repository<ContactMessage, CreateContactMessageInput, Partial<CreateContactMessageInput>> {}

export type SyncOperation = 'create' | 'update' | 'delete';

export interface PendingSyncOperation {
  id: string;
  entity: string;
  entity_id: string;
  operation: SyncOperation;
  payload: unknown;
  created_at: string;
  updated_at: string;
  attempts: number;
  last_error: string | null;
}

interface StoredRecord {
  id: string;
  created_at: string;
  updated_at: string;
}

function createId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export class LocalRepository<T extends StoredRecord, CreateInput, UpdateInput>
  implements Repository<T, CreateInput, UpdateInput>
{
  constructor(
    private readonly storageKey: string,
    private readonly buildRecord: (input: CreateInput, now: string, id: string) => T,
  ) {}

  async list(): Promise<T[]> {
    return this.read();
  }

  async get(id: string): Promise<T | null> {
    return this.read().find((record) => record.id === id) ?? null;
  }

  async create(input: CreateInput): Promise<T> {
    const now = new Date().toISOString();
    const suppliedId = (input as CreateInput & Partial<StoredRecord>).id;
    const record = this.buildRecord(input, now, suppliedId ?? createId());
    this.write([...this.read(), record]);
    return record;
  }

  async update(id: string, input: UpdateInput): Promise<T> {
    const records = this.read();
    const index = records.findIndex((record) => record.id === id);
    if (index === -1) {
      throw new Error(`Record not found: ${id}`);
    }

    const updated = {
      ...records[index],
      ...input,
      updated_at: new Date().toISOString(),
    } as T;
    records[index] = updated;
    this.write(records);
    return updated;
  }

  async remove(id: string): Promise<void> {
    this.write(this.read().filter((record) => record.id !== id));
  }

  private read(): T[] {
    if (typeof localStorage === 'undefined') {
      return [];
    }

    const raw = localStorage.getItem(this.storageKey);
    if (!raw) {
      return [];
    }

    try {
      const value: unknown = JSON.parse(raw);
      return Array.isArray(value) ? (value as T[]) : [];
    } catch {
      return [];
    }
  }

  private write(records: T[]): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(this.storageKey, JSON.stringify(records));
    }
  }
}

export const localProducts = new LocalRepository<Product, CreateProductInput, UpdateProductInput>(
  'siaedts.products',
  (input, now, id) => ({
    ...input,
    id,
    created_at: now,
    updated_at: now,
    published_at: input.status === 'available' ? now : null,
  }),
);

export const localNews = new LocalRepository<News, CreateNewsInput, UpdateNewsInput>(
  'siaedts.news',
  (input, now, id) => ({
    ...input,
    id,
    author_id: 'local-admin',
    created_at: now,
    updated_at: now,
    published_at: input.status === 'published' ? now : null,
  }),
);

export const localGallery = new LocalRepository<Gallery, CreateGalleryInput, Partial<CreateGalleryInput>>(
  'siaedts.gallery',
  (input, now, id) => ({
    ...input,
    id,
    created_at: now,
    updated_at: now,
  }),
);

export const localPartners = new LocalRepository<Partner, CreatePartnerInput, Partial<CreatePartnerInput>>(
  'siaedts.partners',
  (input, now, id) => ({
    ...input,
    id,
    created_at: now,
    updated_at: now,
  }),
);

export const localInquiries = new LocalRepository<Inquiry, CreateInquiryInput, Partial<CreateInquiryInput>>(
  'siaedts.inquiries',
  (input, now, id) => ({
    ...input,
    id,
    status: 'new',
    assigned_to: null,
    notes: null,
    contacted_at: null,
    created_at: now,
    updated_at: now,
  }),
);

export const localMessages = new LocalRepository<ContactMessage, CreateContactMessageInput, Partial<CreateContactMessageInput>>(
  'siaedts.messages',
  (input, now, id) => ({
    ...input,
    id,
    status: 'unread',
    replied_at: null,
    replied_by: null,
    reply_message: null,
    created_at: now,
    updated_at: now,
  }),
);

export const localUsers = new LocalRepository<
  User,
  Omit<User, 'id' | 'created_at' | 'updated_at'>,
  Partial<User>
>(
  'siaedts.users',
  (input, now, id) => ({
    ...input,
    id,
    created_at: now,
    updated_at: now,
  }),
);

export const localTeamMembers = new LocalRepository<
  TeamMember,
  Omit<TeamMember, 'id' | 'created_at' | 'updated_at'>,
  Partial<TeamMember>
>(
  'siaedts.team_members',
  (input, now, id) => ({
    ...input,
    id,
    created_at: now,
    updated_at: now,
  }),
);

export const localSyncQueue = new LocalRepository<PendingSyncOperation, Omit<PendingSyncOperation, 'id' | 'created_at' | 'updated_at' | 'attempts' | 'last_error'>, Partial<PendingSyncOperation>>(
  'siaedts.sync_queue',
  (input, now, id) => ({
    ...input,
    id,
    created_at: now,
    updated_at: now,
    attempts: 0,
    last_error: null,
  }),
);

export async function queueSyncOperation(input: Omit<PendingSyncOperation, 'id' | 'created_at' | 'updated_at' | 'attempts' | 'last_error'>): Promise<PendingSyncOperation> {
  return localSyncQueue.create(input);
}
